import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Divider, Paper, Grid, Box, Stack, Tooltip, IconButton, TextField, Autocomplete
} from '@mui/material';
import { useSnackbar } from 'notistack';
import useConfirm from 'core/hooks/useConfirm';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import MobileDateRangePicker from '@mui/lab/MobileDateRangePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';

import { getDbFormat } from 'utils/common';
import { getLibraryReviewsByDate, deleteLibraryReview } from 'services/feedback';
import { getLibraries } from 'services/commonServices';


const LibraryReviewsPage = () => {
  const defaultLibraryOption = {libraryId: 0, name: 'All'};
  const gridStyle = useMemo(() => ({ minHeight: 480, width: '100%' }), []);
  const [ filterDates, setFilterDates] = React.useState([new Date(), new Date()]);
  const [libraries, setLibraries] = useState([]);
  const [selectedLibrary, setSelectedLibrary] = useState(defaultLibraryOption);
  const [libraryReviews, setLibraryReviews] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();

  const onActionBtnClick = (e, type, reviewId) => {
    if(type === 'delete'){
      onLibraryReviewDelete(reviewId)
    }
  };

  const defaultFilterValue = [
    { name: 'name', operator: 'contains', type: 'string', value: '' },
    { name: 'reviewerName', operator: 'contains', type: 'string', value: '' },
    { name: 'review', operator: 'contains', type: 'string', value: '' },
    { name: 'rating', operator: 'contains', type: 'string', value: '' },
    { name: 'reviewDate', operator: 'contains', type: 'string', value: '' },
  ];

  const columns = [
    { name: 'name', header: 'Library Name', width: 300, render: ({data}) => data?.library?.name ||  data?.libraryId},
    { name: 'reviewerName', header: 'Reviewer Name', width: 200},
    { name: 'review', header: 'Review', width: 600},
    { name: 'rating', header: 'Rating', width: 120},
    { name: 'reviewDate', header: 'Review Date', width: 200},
    { name: 'actions', header: 'Actions', width: 150, render: ({data}) => {
      return (
        <Stack direction="row" spacing={1}>
          <Box>
            <Tooltip title="Delete Review" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'delete', data?.id)}>
                <DeleteForeverIcon color="error" />
              </IconButton>
            </Tooltip>
          </Box> 
        </Stack>
      )
    }},
  ];

  useEffect(() => {
    getLibraryList()
  }, []);

  function getLibraryList() {
    getLibraries().then(({response, error}) => {
      if(response){
        setLibraries([{...defaultLibraryOption}, ...(response || [])])
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    })
  }
  

  function getLibraryReviews() {
    const [fromDate,  toDate] = filterDates;
    const postData = {fromDate: getDbFormat(fromDate), toDate: getDbFormat(toDate)};
    if(selectedLibrary?.libraryId){
      postData['libraryId'] = selectedLibrary?.libraryId;
    }
    getLibraryReviewsByDate({...postData}).then(({response, error}) => {
      if(response){
        setLibraryReviews([...(response || [])]);
      } else {
        setLibraryReviews([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function onDateAccepted(data) {
    const [fromDate,  toDate] = data;
    if((fromDate && toDate)) {
      getLibraryReviews({fromDate: getDbFormat(fromDate), toDate: getDbFormat(toDate)});
    }else{
      enqueueSnackbar(`Select ${fromDate === null ? 'From Date' : 'To Date'}`, {variant: 'error'});
    }
  }

  function onLibraryReviewDelete(feedbackId) {
    confirm({ title: 'Delete library review?', description: 'Are you sure you want proceed?' })
    .then(() => { 
      _deleteLibraryReview(feedbackId);
    }).catch((e)=>{})
  }

  function _deleteLibraryReview(id) {
    deleteLibraryReview({id}).then(({response, error}) => {
      if(response){
        enqueueSnackbar((response?.message || 'Vendor feedback deleted successfully'), {variant: 'success'});
        getLibraryReviews();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function onLibraryChange(event, newValue) {
    setSelectedLibrary(newValue || {...defaultLibraryOption})
  }

  useEffect(() => {
   if(selectedLibrary){
     getLibraryReviews();
   }
  }, [selectedLibrary])
  

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Library Reviews
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={12} xl={12} lg={12} >
            <Paper elevation={0} sx={{p:1, bgcolor: "#f3f3f3"}}>
              <Typography variant="subtitle2" gutterBottom component="div">
                Filters
              </Typography>
              <Stack spacing={1} direction={{ sm: 'column', md: 'row' }} >
                <Box sx={{mt: 2}}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MobileDateRangePicker
                      inputFormat="dd/MM/yyyy"
                      startText="From Date"
                      endText="To Date"
                      value={filterDates}
                      // clearable={true}
                      onChange={(newValue) => {
                        setFilterDates(newValue);
                      }}
                      onAccept={(data) => onDateAccepted(data)}
                      renderInput={(startProps, endProps) => (
                        <React.Fragment>
                          <TextField {...startProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}}/>
                          <Box sx={{ mx: 2 }}> to </Box>
                          <TextField {...endProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}} />
                        </React.Fragment>
                      )}
                      />
                  </LocalizationProvider>
                </Box>
                <Box sx={{}}>
                  <Autocomplete
                  sx={{mt: 2, ml:2, width: 320}}
                    // disablePortal
                    options={libraries}
                    size="small"
                    name="targetEntityId"
                    fullWidth
                    value={selectedLibrary || ''}
                    getOptionLabel={(option) => option?.name || option}
                    isOptionEqualToValue={(option, value) => option?.libraryId === value?.libraryId}
                    onChange={onLibraryChange}
                    renderInput={(params) => <TextField {...params}  required label="Library Name" />}
                  />
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Box component="div" sx={{  mt: 3 ,  maxHeight: 720, width: '100%' }}>
              <ReactDataGrid
                // handle={setGridRef}
                // idProperty="index"
                style={gridStyle}
                defaultFilterValue={defaultFilterValue}
                columns={columns}
                dataSource={libraryReviews}
                rowHeight={null} 
                minRowHeight={50}
              />
            </Box>
          </Grid>
        </Grid>
      </Box> 
    </Paper>
  );
}
 
export default React.memo(LibraryReviewsPage);
